import React, { useState, useMemo, useCallback } from 'react';
import { HeartCanvas } from '../HeartCanvas';
import { Countdown } from '../Countdown';
import type { AdminContentItem, HeartPhrase, HeartSettings } from '../../lib/types';

interface LivePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  settings: HeartSettings;
  lovePhrases: AdminContentItem[];
  nicknames: AdminContentItem[];
}

export const LivePreviewModal: React.FC<LivePreviewModalProps> = ({
  isOpen,
  onClose,
  settings,
  lovePhrases,
  nicknames,
}) => {
  const [seed, setSeed] = useState(() => Math.floor(Math.random() * 1000000));
  const [stats, setStats] = useState<{ total: number; loveRatio: number; nickRatio: number }>({
    total: 0,
    loveRatio: 70,
    nickRatio: 30,
  });

  // Transform active items into heart phrase format (memoized to prevent infinite renders)
  const allPhrases = useMemo<HeartPhrase[]>(() => {
    const activeLove = lovePhrases
      .filter(p => p.isActive)
      .map(p => ({
        id: p.id,
        text: p.phrase || '',
        language: p.language,
        type: 'love' as const,
      }));

    const activeNicks = nicknames
      .filter(n => n.isActive)
      .map(n => ({
        id: n.id,
        text: n.nickname || '',
        type: 'nickname' as const,
      }));

    return [...activeLove, ...activeNicks];
  }, [lovePhrases, nicknames]);

  const handleRearrange = useCallback(() => {
    setSeed(Math.floor(Math.random() * 1000000));
  }, []);

  const handleLayoutReady = useCallback((total: number, loveRatio: number, nickRatio: number) => {
    setStats(prev => {
      if (prev.total === total && prev.loveRatio === loveRatio && prev.nickRatio === nickRatio) {
        return prev;
      }
      return { total, loveRatio, nickRatio };
    });
  }, []);

  const showCountdown =
    (settings.countdown_visibility === 'heart' || settings.countdown_visibility === 'both') &&
    Boolean(settings.countdown_target);

  if (!isOpen) return null;

  return (
    <div className="preview-modal-backdrop" role="dialog" aria-modal="true" aria-label="Heart Live Preview">
      <div className="preview-modal-bar">
        <div className="preview-bar-left">
          <span className="preview-title">Live Preview (Unsaved Settings)</span>
          <span className="preview-meta">
            Rendered: {stats.total} instances ({stats.loveRatio}% Love / {stats.nickRatio}% Nicknames)
          </span>
        </div>

        <div className="preview-bar-right">
          <button
            type="button"
            className="action-btn action-btn-preview"
            onClick={handleRearrange}
          >
            New arrangement
          </button>
          <button
            type="button"
            className="action-btn action-btn-secondary"
            onClick={onClose}
          >
            Exit Preview
          </button>
        </div>
      </div>

      <div className="preview-stage-container" style={{ position: 'relative' }}>
        <HeartCanvas
          phrases={allPhrases}
          settings={settings}
          seed={seed}
          onLayoutReady={handleLayoutReady}
        />

        {showCountdown && (
          <Countdown
            target={settings.countdown_target}
            title={settings.countdown_title}
            variant="heart"
          />
        )}
      </div>
    </div>
  );
};
